import { createContext, useContext, useState, useCallback, useEffect } from 'react'

const NotificationContext = createContext(null)

// Notification types
export const NOTIFICATION_TYPES = {
    INFO: 'info',
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error',
}

/**
 * NotificationProvider - Provides app-wide notification state
 */
export function NotificationProvider({ children, user, subscriptionStatus }) {
    const [notifications, setNotifications] = useState([])

    // Generate unique ID
    const generateId = () => `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Add a new notification
    const addNotification = useCallback((type, title, message, action = null) => {
        const newNotification = {
            id: generateId(),
            type,
            title,
            message,
            action, // { label: string, href: string } or null
            timestamp: new Date(),
            read: false,
        }

        setNotifications(prev => [newNotification, ...prev])
        return newNotification.id
    }, [])

    // Mark notification as read
    const markAsRead = useCallback((id) => {
        setNotifications(prev =>
            prev.map(n => n.id === id ? { ...n, read: true } : n)
        )
    }, [])

    // Mark all as read
    const markAllAsRead = useCallback(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    }, [])

    // Clear a specific notification
    const clearNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(n => n.id !== id))
    }, [])

    // Clear all notifications
    const clearAll = useCallback(() => {
        setNotifications([])
    }, [])

    // Calculate unread count
    const unreadCount = notifications.filter(n => !n.read).length

    // Auto-generate subscription expiration notification
    useEffect(() => {
        if (!user || !subscriptionStatus) return

        // Check if we already have an expiration notification
        const hasExpirationNotif = notifications.some(
            n => n.id?.startsWith('subscription_expiring')
        )

        if (subscriptionStatus.status === 'expiring' && !hasExpirationNotif) {
            const daysRemaining = subscriptionStatus.daysRemaining || 0
            const isUrgent = daysRemaining <= 3

            // Add expiration notification with stable ID (won't duplicate)
            const notif = {
                id: `subscription_expiring_${daysRemaining}`,
                type: isUrgent ? NOTIFICATION_TYPES.ERROR : NOTIFICATION_TYPES.WARNING,
                title: isUrgent ? 'Subscription Expiring Soon!' : 'Subscription Expiring',
                message: `Your Pro access will end in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. Renew to keep your Pro features.`,
                action: { label: 'Renew Now', href: '/#pricing' },
                timestamp: new Date(),
                read: false,
            }

            setNotifications(prev => {
                // Avoid duplicates
                if (prev.some(n => n.id === notif.id)) return prev
                return [notif, ...prev]
            })
        }
    }, [user, subscriptionStatus])

    // Load notifications from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem('inkspot_notifications')
        if (stored) {
            try {
                const parsed = JSON.parse(stored)
                // Convert timestamp strings back to Date objects
                const restored = parsed.map(n => ({
                    ...n,
                    timestamp: new Date(n.timestamp)
                }))
                setNotifications(restored)
            } catch (e) {
                console.error('Failed to restore notifications:', e)
            }
        }
    }, [])

    // Save notifications to localStorage when changed
    useEffect(() => {
        if (notifications.length > 0) {
            localStorage.setItem('inkspot_notifications', JSON.stringify(notifications))
        }
    }, [notifications])

    const value = {
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearAll,
    }

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    )
}

/**
 * useNotifications hook - Access notification state and actions
 */
export function useNotifications() {
    const context = useContext(NotificationContext)
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider')
    }
    return context
}

export default NotificationContext
